import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowsClockwiseIcon as ArrowsClockwise, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, WarningCircleIcon as WarningCircle, ClockIcon as Clock } from "@phosphor-icons/react";

const INTERVALS = [
  { v: 6, l: "Ogni 6 ore" },
  { v: 12, l: "Ogni 12 ore" },
  { v: 24, l: "Ogni giorno" },
  { v: 72, l: "Ogni 3 giorni" },
  { v: 168, l: "Ogni settimana" },
];

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const StatusBadge = ({ r }) => {
  const map = {
    ok: { c: "text-green-700 bg-green-50 border-green-200", i: CheckCircle, t: "OK" },
    error: { c: "text-rose-700 bg-rose-50 border-rose-200", i: XCircle, t: "Errore" },
    skipped: { c: "text-muted-foreground bg-secondary border-border", i: WarningCircle, t: "Saltato" },
  };
  const m = map[r.status] || map.skipped;
  const I = m.i;
  return <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${m.c}`}><I size={12} weight="fill" /> {m.t}</span>;
};

export function SyncScheduleSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);

  const load = () => {
    api.get("/sync/schedule").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/sync/runs").then(({ data }) => setRuns(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return null;
  const status = cfg.providers_status || {};

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/sync/schedule", {
        enabled: cfg.enabled, interval_hours: Number(cfg.interval_hours) || 24, providers: cfg.providers,
      });
      setCfg(data);
      toast.success("Sincronizzazione automatica salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      // Persisti la selezione corrente (provider/intervallo/attivazione) così
      // "Esegui ora" rispetta sempre i toggle mostrati, anche se non salvati.
      const { data: saved } = await api.put("/sync/schedule", {
        enabled: cfg.enabled, interval_hours: Number(cfg.interval_hours) || 24, providers: cfg.providers,
      });
      setCfg(saved);
      const { data } = await api.post("/sync/run-now");
      const ok = (data.results || []).filter((r) => r.status === "ok").length;
      toast.success("Sincronizzazione eseguita", { description: `${ok} provider sincronizzati su ${(data.results || []).length}` });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const toggleProvider = (slug, v) => setCfg((c) => ({ ...c, providers: { ...c.providers, [slug]: v } }));
  const lastRun = runs[0];

  return (
    <Card className="rounded-sm border-border shadow-none p-6 mb-6" data-testid="sync-schedule-section">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><ArrowsClockwise size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Sincronizzazione automatica provider</h3>
            <p className="text-xs text-muted-foreground">Aggiorna periodicamente domini, licenze e dispositivi dai provider collegati (Sophos, RTR, Aruba, CSP, Zyxel, NinjaOne, nic.it)</p>
          </div>
        </div>
        <Button variant="outline" onClick={runNow} disabled={running} className="rounded-sm gap-2" data-testid="sync-run-now-btn">
          <ArrowsClockwise size={16} className={running ? "animate-spin" : ""} /> {running ? "In corso..." : "Esegui ora"}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 items-end mb-5">
        <div className="flex items-center justify-between border border-border rounded-sm p-3">
          <div className="text-sm">Sincronizzazione automatica <span className="block text-xs text-muted-foreground">esegue in background all'intervallo scelto</span></div>
          <Switch data-testid="sync-enabled-switch" checked={!!cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Frequenza</Label>
          <Select value={String(cfg.interval_hours)} onValueChange={(v) => setCfg((c) => ({ ...c, interval_hours: Number(v) }))}>
            <SelectTrigger className="mt-1 rounded-sm bg-white" data-testid="sync-interval-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INTERVALS.map((i) => <SelectItem key={i.v} value={String(i.v)}>{i.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-5">
        <Label className="text-xs uppercase tracking-wider mb-2 block">Provider da sincronizzare</Label>
        <div className="grid sm:grid-cols-2 gap-2" data-testid="sync-providers-list">
          {Object.entries(status).map(([slug, meta]) => (
            <label key={slug} className="flex items-center justify-between border border-border rounded-sm px-3 py-2" data-testid={`sync-provider-${slug}`}>
              <span className="flex items-center gap-2 text-sm">
                {meta.label}
                {meta.configured
                  ? <span className="text-[10px] text-green-700">· configurato</span>
                  : <span className="text-[10px] text-amber-600">· non configurato</span>}
              </span>
              <Switch data-testid={`sync-provider-switch-${slug}`} checked={!!cfg.providers?.[slug]} onCheckedChange={(v) => toggleProvider(slug, v)} />
            </label>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">I provider non configurati vengono automaticamente saltati durante la sincronizzazione.</p>
      </div>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="save-sync-schedule-btn">{saving ? "Salvataggio..." : "Salva impostazioni"}</Button>
        {cfg.last_run && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock size={13} /> Ultima esecuzione: {fmtDT(cfg.last_run)}</span>
        )}
        {cfg.enabled && cfg.next_run && (
          <span className="text-xs text-muted-foreground">· Prossima prevista: {fmtDT(cfg.next_run)}</span>
        )}
      </div>

      {lastRun && (
        <div className="border-t border-border pt-4" data-testid="sync-last-run">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Ultima esecuzione ({lastRun.trigger === "auto" ? "automatica" : "manuale"}) · {fmtDT(lastRun.finished_at)}</div>
          <div className="space-y-1.5">
            {(lastRun.results || []).map((r) => (
              <div key={r.provider} className="flex items-center justify-between text-sm gap-2" data-testid={`sync-result-${r.provider}`}>
                <span className="text-foreground">{r.label}</span>
                <span className="flex items-center gap-2 min-w-0">
                  {r.status === "ok" && r.result && (
                    <span className="text-xs text-muted-foreground truncate">
                      {[r.result.nuovi != null ? `${r.result.nuovi} nuovi` : null, r.result.aggiornati != null ? `${r.result.aggiornati} agg.` : null, r.result.collegati != null ? `${r.result.collegati} coll.` : null].filter(Boolean).join(" · ") || "completato"}
                    </span>
                  )}
                  {r.status === "error" && <span className="text-xs text-rose-600 truncate max-w-[220px]" title={r.message}>{r.message}</span>}
                  <StatusBadge r={r} />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
